import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Mail, Send, Smartphone, Trash2, ShieldAlert, CheckCircle2, HelpCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { currentPushSubscription, disablePush, enablePush, isIos, isPushSupported, isStandalone } from "@/lib/pwa";

const CATEGORIES = [
  { key: "daily_briefing", label: "Daily AI briefing", desc: "Your morning executive briefing." },
  { key: "task_reminders", label: "Task reminders", desc: "When a task's reminder time fires." },
  { key: "sales_followups", label: "Sales follow-ups", desc: "Deals whose next follow-up is due." },
  { key: "meetings", label: "Meetings", desc: "Upcoming calendar events." },
  { key: "project_deadlines", label: "Project deadlines", desc: "Milestones and project due dates." },
  { key: "important_alerts", label: "Important alerts", desc: "Security and account activity." },
];

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hasThisDevice, setHasThisDevice] = useState(false);
  const [browserPermission, setBrowserPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/notification-preferences"),
        api.get("/push/subscriptions"),
      ]);
      setPrefs(p.data.preferences);
      setDevices(s.data.subscriptions || []);
    } catch { /* ignore */ }
    const sub = await currentPushSubscription().catch(() => null);
    setHasThisDevice(!!sub);
    if (typeof Notification !== "undefined") setBrowserPermission(Notification.permission);
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePref = async (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    try {
      const { data } = await api.patch("/notification-preferences", { [key]: value });
      setPrefs(data.preferences);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save preference.");
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push notifications enabled on this device.");
      await load();
    } catch (err) {
      toast.error(err.message || "Could not enable push notifications.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Push disabled on this device.");
      await load();
    } catch { toast.error("Could not disable."); } finally { setBusy(false); }
  };

  const testPush = async () => {
    try { await api.post("/push/test"); toast.success("Test push sent."); }
    catch { toast.error("Could not send test push."); }
  };

  const removeDevice = async (id) => {
    try {
      await api.delete(`/push/subscriptions/${id}`);
      setDevices((d) => d.filter((x) => x.id !== id));
    } catch { toast.error("Could not remove device."); }
  };

  return (
    <div className="space-y-4" data-testid="notification-settings">
      <Seo title="Notifications" description="Notification preferences and devices." path="/app/settings/notifications" />
      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
        <h2 className="font-display text-xl font-semibold tracking-tight">Notifications</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose how you'd like Workspace to reach you — push on installed devices, email as a reliable fallback, or both.</p>

        {/* This-device push toggle */}
        <div className="mt-5 rounded-2xl border border-border bg-background/40 p-4" data-testid="push-device-card">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-brand-teal"><Smartphone className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold">This device</p>
                <NotificationStatusPill supported={isPushSupported()} permission={browserPermission} subscribed={hasThisDevice} />
              </div>
              {!isPushSupported() ? (
                <p className="mt-1 text-xs text-muted-foreground">Push isn't supported in this browser.</p>
              ) : isIos() && !isStandalone() ? (
                <p className="mt-1 text-xs text-muted-foreground">On iPhone/iPad, first add Workspace to your Home Screen — then open the app icon and enable push here.</p>
              ) : browserPermission === "denied" ? (
                <div className="mt-2 space-y-2" data-testid="push-blocked-help">
                  <p className="text-xs text-muted-foreground">Your browser has <span className="font-semibold text-brand-red">blocked</span> notifications for this site. To re-enable:</p>
                  <ol className="ml-4 list-decimal text-[11px] text-muted-foreground">
                    <li>Click the lock icon (or "site info") next to the URL.</li>
                    <li>Set <span className="font-semibold">Notifications</span> to <span className="font-semibold">Allow</span>.</li>
                    <li>Reload this page and click <span className="font-semibold">Re-enable push</span>.</li>
                  </ol>
                  <button onClick={enable} disabled={busy} data-testid="push-reenable-btn" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium"><Bell className="h-3.5 w-3.5" /> Re-enable push</button>
                </div>
              ) : hasThisDevice ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={disable} disabled={busy} data-testid="push-disable-btn" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium"><BellOff className="h-3.5 w-3.5" /> Disable push</button>
                  <button onClick={testPush} data-testid="push-test-btn" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium"><Send className="h-3.5 w-3.5" /> Send test push</button>
                </div>
              ) : (
                <button onClick={enable} disabled={busy} data-testid="push-enable-btn" className="mt-2 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">
                  <Bell className="h-3.5 w-3.5" /> Enable push on this device
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Preferences */}
        {prefs && (
          <div className="mt-5 rounded-2xl border border-border bg-background/40 p-4" data-testid="notif-prefs-card">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <p className="text-sm font-semibold">Channels</p>
            </div>
            <div className="divide-y divide-border">
              <PrefRow label="Push notifications" desc="On subscribed devices." checked={prefs.push_enabled} onChange={(v) => savePref("push_enabled", v)} testid="pref-push_enabled" />
              <PrefRow label="Email notifications" desc="Reliable fallback delivery." icon={Mail} checked={prefs.email_enabled} onChange={(v) => savePref("email_enabled", v)} testid="pref-email_enabled" />
            </div>
            <div className="mt-3 flex items-center justify-between border-b border-border pb-2">
              <p className="text-sm font-semibold">Categories</p>
            </div>
            <div className="divide-y divide-border">
              {CATEGORIES.map((c) => (
                <PrefRow key={c.key} label={c.label} desc={c.desc} checked={prefs[c.key]} onChange={(v) => savePref(c.key, v)} testid={`pref-${c.key}`} />
              ))}
            </div>
          </div>
        )}

        {/* Devices */}
        <div className="mt-5 rounded-2xl border border-border bg-background/40 p-4" data-testid="devices-card">
          <p className="text-sm font-semibold">Subscribed devices</p>
          <p className="mt-0.5 text-xs text-muted-foreground">You can subscribe multiple devices — desktop, phone, tablet.</p>
          {devices.length === 0 ? (
            <p className="mt-3 text-xs text-muted-foreground">No subscribed devices yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {devices.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 rounded-xl bg-card px-3 py-2 text-xs" data-testid={`device-${d.id}`}>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{d.device_label || "Unknown device"}</p>
                    <p className="text-[10px] text-muted-foreground">{d.timezone || "UTC"} · added {(d.created_at || "").slice(0, 10)}</p>
                  </div>
                  <button onClick={() => removeDevice(d.id)} data-testid={`device-remove-${d.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function PrefRow({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {desc && <p className="text-[11px] text-muted-foreground">{desc}</p>}
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-brand-teal" : "bg-muted"}`}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

function NotificationStatusPill({ supported, permission, subscribed }) {
  if (!supported) {
    return (
      <span data-testid="notif-status-pill" className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <HelpCircle className="h-3 w-3" /> Not supported
      </span>
    );
  }
  if (permission === "denied") {
    return (
      <span data-testid="notif-status-pill" className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-red ring-1 ring-red-500/30">
        <ShieldAlert className="h-3 w-3" /> Blocked by browser
      </span>
    );
  }
  if (subscribed && permission === "granted") {
    return (
      <span data-testid="notif-status-pill" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-green ring-1 ring-emerald-500/30">
        <CheckCircle2 className="h-3 w-3" /> Enabled
      </span>
    );
  }
  return (
    <span data-testid="notif-status-pill" className="inline-flex items-center gap-1 rounded-full bg-orange-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-orange ring-1 ring-orange-500/30">
      <Bell className="h-3 w-3" /> Not configured
    </span>
  );
}
